import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check, ArrowRight, ArrowLeft, MapPin, Phone, Clock, Calendar as CalendarIcon,
  Loader2, AlertCircle, ChevronLeft, ChevronRight, X,
} from "lucide-react";
import { pt } from "date-fns/locale";
import { DayPicker } from "react-day-picker";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import SeoHead from "../components/SeoHead";
import {
  listarClinicas, listarServicosOnline, listarTodosServicos,
  getDisponibilidade, criarMarcacao, icsUrl,
} from "../lib/api";
import { combinarDataHora, formatarDataHoraPT, isoLocal } from "../lib/datetime";

const STEPS = [
  { n: 1, label: "Clínica" },
  { n: 2, label: "Serviço" },
  { n: 3, label: "Data e hora" },
  { n: 4, label: "Dados" },
  { n: 5, label: "Confirmar" },
];

const CATEGORIA_NOME = {
  preventiva: "Medicina Preventiva",
  diagnostico: "Diagnóstico",
  cirurgia: "Cirurgia",
  urgencia: "Urgências",
  outros: "Outros Cuidados",
};

const ESPECIES = [
  { v: "cao", l: "Cão" },
  { v: "gato", l: "Gato" },
  { v: "coelho", l: "Coelho" },
  { v: "ave", l: "Ave" },
  { v: "reptil", l: "Réptil" },
  { v: "outro", l: "Outro" },
];

// ------- UI primitives --------
function Stepper({ current }) {
  const pct = ((current - 1) / (STEPS.length - 1)) * 100;
  return (
    <div className="mb-10" data-testid="wizard-stepper">
      <div className="relative h-2 rounded-full bg-[#1E5F6E]/10 overflow-hidden mb-5">
        <motion.div
          className="absolute inset-y-0 left-0 bg-[#1E5F6E]"
          initial={false}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.4 }}
        />
      </div>
      <ol className="grid grid-cols-5 gap-2 text-center">
        {STEPS.map((s) => {
          const done = s.n < current;
          const active = s.n === current;
          return (
            <li key={s.n} className="flex flex-col items-center gap-1.5">
              <span
                data-testid={`step-${s.n}`}
                className={`grid h-8 w-8 place-items-center rounded-full text-xs font-bold transition-colors ${
                  active
                    ? "bg-[#1E5F6E] text-white"
                    : done
                    ? "bg-[#88A096] text-white"
                    : "bg-white border border-[#1E5F6E]/15 text-[#5A5A5A]"
                }`}
              >
                {done ? <Check className="h-4 w-4" /> : s.n}
              </span>
              <span
                className={`text-[11px] sm:text-xs font-semibold tracking-wide ${
                  active ? "text-[#1E5F6E]" : "text-[#5A5A5A]"
                }`}
              >
                {s.label}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function NavRow({ onBack, onNext, nextDisabled, nextLabel = "Continuar", loading, nextTestId = "wizard-next" }) {
  return (
    <div className="mt-10 flex flex-col-reverse sm:flex-row gap-3 sm:justify-between">
      {onBack ? (
        <button
          onClick={onBack}
          data-testid="wizard-back"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-white border-2 border-[#1E5F6E]/15 px-6 py-3 text-sm font-bold text-[#1E5F6E] hover:border-[#1E5F6E]/40 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </button>
      ) : <span />}
      <button
        onClick={onNext}
        disabled={nextDisabled || loading}
        data-testid={nextTestId}
        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1E5F6E] px-7 py-3.5 text-sm font-bold text-white hover:bg-[#174E5B] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {nextLabel}
        {!loading && <ArrowRight className="h-4 w-4" />}
      </button>
    </div>
  );
}

// ------- Step 1: Clinic --------
function StepClinica({ clinicas, value, onChange }) {
  return (
    <div data-testid="step-clinica">
      <h2 className="font-display text-2xl md:text-3xl font-bold text-[#1A1A1A] tracking-tight mb-2">
        Onde prefere ser <span className="text-[#1E5F6E]">atendido?</span>
      </h2>
      <p className="text-[#5A5A5A] mb-8">Escolha uma das nossas duas clínicas na Madeira.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {clinicas.map((c) => {
          const sel = value === c.slug;
          return (
            <button
              key={c.slug}
              onClick={() => onChange(c.slug)}
              data-testid={`clinic-option-${c.slug}`}
              className={`group text-left rounded-3xl p-6 md:p-7 border-2 transition-all ${
                sel
                  ? "bg-[#1E5F6E] text-white border-[#1E5F6E] shadow-glow"
                  : "bg-white border-[#1E5F6E]/10 hover:border-[#1E5F6E]/40 hover:-translate-y-1 hover:shadow-soft"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className={`text-xs uppercase tracking-widest font-bold ${sel ? "text-white/70" : "text-[#88A096]"}`}>
                    Clínica
                  </div>
                  <h3 className={`mt-1 font-display text-xl md:text-2xl font-extrabold ${sel ? "text-white" : "text-[#1A1A1A]"}`}>
                    {c.nome}
                  </h3>
                </div>
                <span className={`grid h-10 w-10 place-items-center rounded-xl ${sel ? "bg-white/15" : "bg-[#1E5F6E]/8"}`}>
                  {sel ? <Check className="h-5 w-5" /> : <MapPin className={`h-5 w-5 ${sel ? "" : "text-[#1E5F6E]"}`} />}
                </span>
              </div>
              <div className={`mt-5 space-y-2 text-sm ${sel ? "text-white/85" : "text-[#5A5A5A]"}`}>
                <div className="flex items-start gap-2">
                  <MapPin className={`h-4 w-4 mt-0.5 ${sel ? "text-white/70" : "text-[#88A096]"}`} />
                  <span>{c.morada}, {c.codigo_postal} {c.cidade}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className={`h-4 w-4 ${sel ? "text-white/70" : "text-[#88A096]"}`} />
                  <span>{c.telefone}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ------- Step 2: Service --------
function StepServico({ servicos, todos, clinicaSelecionada, value, onChange }) {
  const grupos = useMemo(() => {
    const byCat = {};
    servicos.forEach((s) => {
      (byCat[s.categoria] = byCat[s.categoria] || []).push(s);
    });
    return byCat;
  }, [servicos]);

  const requerTelefone = useMemo(
    () => todos.filter((s) => !s.disponivel_marcacao_online && ["cirurgia", "urgencia"].includes(s.categoria))
      .concat(todos.filter((s) => s.slug === "hospitalizacao")),
    [todos]
  );

  return (
    <div data-testid="step-servico">
      <h2 className="font-display text-2xl md:text-3xl font-bold text-[#1A1A1A] tracking-tight mb-2">
        Que <span className="text-[#1E5F6E]">serviço</span> procura?
      </h2>
      <p className="text-[#5A5A5A] mb-8">Mostramos apenas serviços marcáveis online.</p>

      <div className="space-y-8">
        {Object.entries(grupos).map(([cat, items]) => (
          <div key={cat}>
            <h3 className="font-display text-sm font-bold uppercase tracking-widest text-[#88A096] mb-3">
              {CATEGORIA_NOME[cat] || cat}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {items.map((s) => {
                const sel = value === s.slug;
                return (
                  <button
                    key={s.slug}
                    onClick={() => onChange(s.slug)}
                    data-testid={`service-option-${s.slug}`}
                    className={`text-left rounded-2xl p-5 border-2 transition-all ${
                      sel
                        ? "bg-[#1E5F6E] text-white border-[#1E5F6E]"
                        : "bg-white border-[#1E5F6E]/10 hover:border-[#1E5F6E]/40 hover:-translate-y-0.5"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className={`font-display font-bold ${sel ? "text-white" : "text-[#1A1A1A]"}`}>
                        {s.nome}
                      </div>
                      {sel && <Check className="h-5 w-5 text-white" />}
                    </div>
                    <p className={`mt-1.5 text-[13.5px] leading-relaxed ${sel ? "text-white/85" : "text-[#5A5A5A]"}`}>
                      {s.descricao}
                    </p>
                    <div className={`mt-3 text-xs font-semibold ${sel ? "text-white/80" : "text-[#5A5A5A]"}`}>
                      Duração: <span className={sel ? "text-white" : "text-[#1A1A1A]"}>{s.duracao_minutos} min</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {requerTelefone.length > 0 && clinicaSelecionada && (
        <div
          data-testid="service-call-only"
          className="mt-10 rounded-2xl bg-[#E8A87C]/10 border border-[#E8A87C]/30 p-6"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-[#7a3e1a] mt-0.5 shrink-0" />
            <div>
              <h4 className="font-display text-lg font-bold text-[#1A1A1A]">
                Estes serviços requerem contacto telefónico
              </h4>
              <p className="text-sm text-[#5A5A5A] mt-1">
                Para cirurgias, urgências e hospitalização contacte-nos diretamente para um atendimento personalizado.
              </p>
              <ul className="mt-3 flex flex-wrap gap-2">
                {requerTelefone.map((s) => (
                  <li key={s.slug} className="text-xs font-semibold rounded-full bg-white border border-[#E8A87C]/40 text-[#7a3e1a] px-3 py-1">
                    {s.nome}
                  </li>
                ))}
              </ul>
              <a
                href={`tel:${clinicaSelecionada.telefone_e164}`}
                data-testid="call-clinic-btn"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#1E5F6E] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#174E5B] transition-colors"
              >
                <Phone className="h-4 w-4" />
                Ligar à {clinicaSelecionada.nome}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ------- Step 3: Date + slot --------
function StepDataHora({ clinicaSlug, servicoSlug, value, onChange }) {
  const [date, setDate] = useState(value?.date || null);
  const [hora, setHora] = useState(value?.hora || null);
  const [slots, setSlots] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [razao, setRazao] = useState(null);

  useEffect(() => {
    if (!date) {
      setSlots([]);
      return;
    }
    let active = true;
    setCarregando(true);
    setRazao(null);
    getDisponibilidade({
      clinica: clinicaSlug,
      servico: servicoSlug,
      data: isoLocal(date),
    })
      .then((r) => {
        if (!active) return;
        setSlots(r.slots || []);
        setRazao(r.razao || null);
      })
      .catch(() => {
        if (!active) return;
        setSlots([]);
        setRazao("Erro a obter disponibilidade. Tente novamente.");
      })
      .finally(() => active && setCarregando(false));
    return () => {
      active = false;
    };
  }, [date, clinicaSlug, servicoSlug]);

  useEffect(() => {
    onChange({ date, hora });
  }, [date, hora, onChange]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 60);

  const disabledMatcher = (d) => {
    if (d < today) return true;
    if (d > maxDate) return true;
    // close Sundays
    if (d.getDay() === 0) return true;
    return false;
  };

  return (
    <div data-testid="step-data-hora">
      <h2 className="font-display text-2xl md:text-3xl font-bold text-[#1A1A1A] tracking-tight mb-2">
        Escolha uma <span className="text-[#1E5F6E]">data e hora</span>.
      </h2>
      <p className="text-[#5A5A5A] mb-8">
        As datas a vermelho estão indisponíveis (domingos, dias passados ou fora da janela de marcação).
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="rounded-3xl bg-white border border-[#1E5F6E]/10 p-3 md:p-5 shadow-soft" data-testid="datepicker">
          <DayPicker
            mode="single"
            selected={date}
            onSelect={(d) => {
              setHora(null);
              setDate(d || null);
            }}
            locale={pt}
            disabled={disabledMatcher}
            fromDate={today}
            toDate={maxDate}
            showOutsideDays
            classNames={{
              months: "flex flex-col",
              month: "space-y-4 w-full",
              caption: "flex items-center justify-center pt-1 relative",
              caption_label: "text-base font-display font-bold text-[#1A1A1A] capitalize",
              nav: "absolute inset-x-0 flex justify-between px-2",
              nav_button: "h-8 w-8 inline-flex items-center justify-center rounded-lg bg-white border border-[#1E5F6E]/15 hover:border-[#1E5F6E]/40 text-[#1E5F6E] transition-colors",
              table: "w-full border-collapse",
              head_row: "flex w-full",
              head_cell: "flex-1 text-[11px] text-[#5A5A5A] font-bold uppercase tracking-wider py-2",
              row: "flex w-full",
              cell: "flex-1 p-0.5",
              day: "h-10 w-full inline-flex items-center justify-center rounded-lg text-sm font-medium text-[#1A1A1A] hover:bg-[#1E5F6E]/10 transition-colors",
              day_selected: "!bg-[#1E5F6E] !text-white hover:!bg-[#1E5F6E]",
              day_today: "ring-1 ring-[#E8A87C]",
              day_disabled: "!text-rose-300 line-through cursor-not-allowed hover:!bg-transparent",
              day_outside: "text-[#5A5A5A]/30",
            }}
            components={{
              IconLeft: () => <ChevronLeft className="h-4 w-4" />,
              IconRight: () => <ChevronRight className="h-4 w-4" />,
            }}
          />
        </div>

        <div data-testid="slots-area">
          <div className="rounded-3xl bg-white border border-[#1E5F6E]/10 p-5 md:p-6 min-h-[280px] shadow-soft">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-5 w-5 text-[#1E5F6E]" />
              <h3 className="font-display text-lg font-bold text-[#1A1A1A]">
                Horários disponíveis
              </h3>
            </div>

            {!date && (
              <p className="text-sm text-[#5A5A5A]">
                Escolha uma data no calendário para ver os horários disponíveis.
              </p>
            )}

            {date && carregando && (
              <div className="flex items-center gap-2 text-sm text-[#5A5A5A]">
                <Loader2 className="h-4 w-4 animate-spin" />
                A procurar horários...
              </div>
            )}

            {date && !carregando && razao && (
              <div className="rounded-xl bg-[#E8A87C]/10 border border-[#E8A87C]/30 p-4 text-sm text-[#7a3e1a]">
                {razao}
              </div>
            )}

            {date && !carregando && !razao && slots.length === 0 && (
              <div className="rounded-xl bg-[#E8A87C]/10 border border-[#E8A87C]/30 p-4 text-sm text-[#7a3e1a]">
                Sem horários disponíveis neste dia. Tente outra data.
              </div>
            )}

            {date && !carregando && slots.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {slots.map((s) => {
                  const sel = hora === s.hora;
                  return (
                    <button
                      key={s.hora}
                      disabled={!s.disponivel}
                      onClick={() => setHora(s.hora)}
                      data-testid={`slot-${s.hora}`}
                      className={`rounded-xl py-2.5 text-sm font-semibold transition-all ${
                        sel
                          ? "bg-[#1E5F6E] text-white"
                          : s.disponivel
                          ? "bg-[#FAF7F2] text-[#1A1A1A] hover:bg-[#1E5F6E]/10"
                          : "bg-[#FAF7F2] text-[#5A5A5A]/40 line-through cursor-not-allowed"
                      }`}
                    >
                      {s.hora}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ------- Step 4: Form --------
function StepDados({ value, onChange, erros }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const setAnimal = (k, v) => onChange({ ...value, animal: { ...value.animal, [k]: v } });

  const motivoCount = (value.motivo || "").length;

  const InputErr = ({ name }) => erros[name] ? (
    <p className="mt-1 text-xs font-semibold text-rose-600" data-testid={`erro-${name}`}>{erros[name]}</p>
  ) : null;

  const Field = ({ label, children, required }) => (
    <label className="block">
      <span className="block text-sm font-semibold text-[#1A1A1A] mb-1.5">
        {label} {required && <span className="text-rose-500">*</span>}
      </span>
      {children}
    </label>
  );

  const inputCls = "w-full rounded-xl bg-white border-2 border-[#1E5F6E]/10 px-4 py-3 text-sm focus:border-[#1E5F6E] focus:outline-none transition-colors";

  return (
    <div data-testid="step-dados" className="space-y-10">
      <h2 className="font-display text-2xl md:text-3xl font-bold text-[#1A1A1A] tracking-tight">
        Os seus <span className="text-[#1E5F6E]">dados</span>.
      </h2>

      {/* Honeypot */}
      <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden>
        <label>
          Website
          <input type="text" tabIndex="-1" autoComplete="off"
            value={value.website || ""}
            onChange={(e) => set("website", e.target.value)} />
        </label>
      </div>

      <section>
        <h3 className="font-display text-sm font-bold uppercase tracking-widest text-[#88A096] mb-4">Sobre si</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Nome completo" required>
            <input data-testid="input-nome" className={inputCls} value={value.nome} onChange={(e) => set("nome", e.target.value)} />
            <InputErr name="nome" />
          </Field>
          <Field label="Email" required>
            <input type="email" data-testid="input-email" className={inputCls} value={value.email} onChange={(e) => set("email", e.target.value)} />
            <InputErr name="email" />
          </Field>
          <Field label="Telemóvel" required>
            <input data-testid="input-telefone" placeholder="9XX XXX XXX" className={inputCls} value={value.telefone} onChange={(e) => set("telefone", e.target.value)} />
            <InputErr name="telefone" />
          </Field>
          <Field label="É a primeira vez nesta clínica?" required>
            <div className="flex gap-3 mt-1">
              {[{ v: true, l: "Sim" }, { v: false, l: "Não" }].map((o) => {
                const sel = value.primeira_vez === o.v;
                return (
                  <button
                    key={String(o.v)}
                    type="button"
                    onClick={() => set("primeira_vez", o.v)}
                    data-testid={`primeira-vez-${o.l.toLowerCase()}`}
                    className={`flex-1 rounded-xl py-3 text-sm font-bold border-2 transition-colors ${
                      sel ? "bg-[#1E5F6E] text-white border-[#1E5F6E]" : "bg-white text-[#1A1A1A] border-[#1E5F6E]/10 hover:border-[#1E5F6E]/40"
                    }`}
                  >
                    {o.l}
                  </button>
                );
              })}
            </div>
            <InputErr name="primeira_vez" />
          </Field>
        </div>
      </section>

      <section>
        <h3 className="font-display text-sm font-bold uppercase tracking-widest text-[#88A096] mb-4">Sobre o seu animal</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Nome do animal" required>
            <input data-testid="input-animal-nome" className={inputCls} value={value.animal.nome} onChange={(e) => setAnimal("nome", e.target.value)} />
            <InputErr name="animal_nome" />
          </Field>
          <Field label="Espécie" required>
            <select
              data-testid="input-animal-especie"
              className={inputCls}
              value={value.animal.especie || ""}
              onChange={(e) => setAnimal("especie", e.target.value)}
            >
              <option value="">— Selecione —</option>
              {ESPECIES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
            <InputErr name="animal_especie" />
          </Field>
          <Field label="Raça">
            <input data-testid="input-animal-raca" className={inputCls} value={value.animal.raca} onChange={(e) => setAnimal("raca", e.target.value)} />
          </Field>
          <Field label="Idade aproximada (anos)">
            <input type="number" step="0.5" min="0" max="40" data-testid="input-animal-idade" className={inputCls}
              value={value.animal.idade_anos} onChange={(e) => setAnimal("idade_anos", e.target.value)} />
          </Field>
          <Field label="Sexo">
            <div className="flex gap-2 flex-wrap">
              {[{ v: "macho", l: "Macho" }, { v: "femea", l: "Fêmea" }, { v: "desconhecido", l: "Não sei" }].map((o) => {
                const sel = value.animal.sexo === o.v;
                return (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setAnimal("sexo", o.v)}
                    data-testid={`animal-sexo-${o.v}`}
                    className={`rounded-full px-4 py-2 text-xs font-bold border-2 transition-colors ${
                      sel ? "bg-[#1E5F6E] text-white border-[#1E5F6E]" : "bg-white text-[#1A1A1A] border-[#1E5F6E]/10 hover:border-[#1E5F6E]/40"
                    }`}
                  >
                    {o.l}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>
      </section>

      <section>
        <h3 className="font-display text-sm font-bold uppercase tracking-widest text-[#88A096] mb-4">Motivo</h3>
        <Field label="Motivo da consulta ou sintomas (opcional)">
          <textarea
            data-testid="input-motivo"
            rows={4}
            maxLength={500}
            className={inputCls}
            value={value.motivo}
            onChange={(e) => set("motivo", e.target.value)}
            placeholder="Descreva sintomas ou o objetivo da consulta..."
          />
          <div className="mt-1 text-right text-xs text-[#5A5A5A]">{motivoCount}/500</div>
        </Field>
      </section>

      <section>
        <h3 className="font-display text-sm font-bold uppercase tracking-widest text-[#88A096] mb-4">Consentimentos</h3>
        <div className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="checkbox-marketing"
              checked={value.consentimento_marketing}
              onChange={(e) => set("consentimento_marketing", e.target.checked)}
              className="mt-1 h-4 w-4 accent-[#1E5F6E]"
            />
            <span className="text-sm text-[#1A1A1A]">
              Aceito receber comunicações por email sobre lembretes e novidades.
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="checkbox-politica"
              checked={value.aceita_politica}
              onChange={(e) => set("aceita_politica", e.target.checked)}
              className="mt-1 h-4 w-4 accent-[#1E5F6E]"
            />
            <span className="text-sm text-[#1A1A1A]">
              Li e aceito a{" "}
              <a href="/politica-de-privacidade" target="_blank" rel="noreferrer" className="text-[#1E5F6E] font-bold underline underline-offset-4">política de privacidade</a>{" "}
              e os{" "}
              <a href="/termos-e-condicoes" target="_blank" rel="noreferrer" className="text-[#1E5F6E] font-bold underline underline-offset-4">termos e condições</a>. <span className="text-rose-500">*</span>
            </span>
          </label>
          <InputErr name="aceita_politica" />
        </div>
      </section>
    </div>
  );
}

// ------- Step 5: Summary --------
function StepConfirmacao({ clinica, servico, dataHora, dados }) {
  return (
    <div data-testid="step-confirmacao">
      <h2 className="font-display text-2xl md:text-3xl font-bold text-[#1A1A1A] tracking-tight mb-2">
        Confirmar <span className="text-[#1E5F6E]">marcação</span>.
      </h2>
      <p className="text-[#5A5A5A] mb-8">Verifique tudo antes de submeter.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="rounded-2xl bg-white border border-[#1E5F6E]/10 p-6">
          <div className="text-xs font-bold uppercase tracking-widest text-[#88A096] mb-2">Clínica</div>
          <div className="font-display text-lg font-bold text-[#1A1A1A]">{clinica.nome}</div>
          <div className="text-sm text-[#5A5A5A] mt-1.5">{clinica.morada}, {clinica.codigo_postal} {clinica.cidade}</div>
          <div className="text-sm text-[#1E5F6E] font-bold mt-2">{clinica.telefone}</div>
        </div>
        <div className="rounded-2xl bg-white border border-[#1E5F6E]/10 p-6">
          <div className="text-xs font-bold uppercase tracking-widest text-[#88A096] mb-2">Serviço</div>
          <div className="font-display text-lg font-bold text-[#1A1A1A]">{servico.nome}</div>
          <div className="text-sm text-[#5A5A5A] mt-1.5">Duração: {servico.duracao_minutos} min</div>
        </div>
        <div className="rounded-2xl bg-[#1E5F6E] text-white p-6 md:col-span-2">
          <div className="text-xs font-bold uppercase tracking-widest text-white/70 mb-2">Quando</div>
          <div className="font-display text-xl md:text-2xl font-extrabold">
            {formatarDataHoraPT(dataHora)}
          </div>
        </div>
        <div className="rounded-2xl bg-white border border-[#1E5F6E]/10 p-6">
          <div className="text-xs font-bold uppercase tracking-widest text-[#88A096] mb-2">Tutor</div>
          <div className="font-bold text-[#1A1A1A]">{dados.nome}</div>
          <div className="text-sm text-[#5A5A5A]">{dados.email}</div>
          <div className="text-sm text-[#5A5A5A]">{dados.telefone}</div>
        </div>
        <div className="rounded-2xl bg-white border border-[#1E5F6E]/10 p-6">
          <div className="text-xs font-bold uppercase tracking-widest text-[#88A096] mb-2">Animal</div>
          <div className="font-bold text-[#1A1A1A]">{dados.animal.nome} <span className="text-[#5A5A5A] font-normal">— {ESPECIES.find((e) => e.v === dados.animal.especie)?.l || "—"}</span></div>
          {dados.animal.raca && <div className="text-sm text-[#5A5A5A]">{dados.animal.raca}</div>}
          {dados.animal.idade_anos !== "" && <div className="text-sm text-[#5A5A5A]">{dados.animal.idade_anos} {Number(dados.animal.idade_anos) === 1 ? "ano" : "anos"}</div>}
        </div>
        {dados.motivo && (
          <div className="rounded-2xl bg-white border border-[#1E5F6E]/10 p-6 md:col-span-2">
            <div className="text-xs font-bold uppercase tracking-widest text-[#88A096] mb-2">Motivo</div>
            <p className="text-sm text-[#1A1A1A] whitespace-pre-line">{dados.motivo}</p>
          </div>
        )}
      </div>

      <div className="mt-6 rounded-2xl bg-[#E8A87C]/10 border border-[#E8A87C]/30 p-5 text-sm text-[#7a3e1a]" data-testid="aviso-validacao">
        Receberá um email de confirmação. A marcação fica <strong>pendente</strong> de validação pela clínica, que confirmará por email ou telefone.
      </div>
    </div>
  );
}

// ------- Success --------
function Sucesso({ marcacao, onReset }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      data-testid="success-screen"
      className="text-center"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.15, type: "spring", stiffness: 220 }}
        className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-emerald-100 text-emerald-700 mb-6"
      >
        <Check className="h-10 w-10" strokeWidth={3} />
      </motion.div>
      <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#1A1A1A] tracking-tight">
        Marcação <span className="text-[#1E5F6E]">recebida!</span>
      </h1>
      <p className="mt-4 text-[#5A5A5A]">Guardámos a sua marcação com o número:</p>
      <div data-testid="success-numero" className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-[#1E5F6E] text-white px-6 py-3 font-display text-xl md:text-2xl font-extrabold tracking-wider">
        {marcacao.numero_marcacao}
      </div>

      <div className="mt-8 max-w-xl mx-auto rounded-3xl bg-white border border-[#1E5F6E]/10 p-6 md:p-8 text-left shadow-soft">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[#88A096]">Quando</div>
            <div className="mt-1 font-bold text-[#1A1A1A] leading-snug">
              {formatarDataHoraPT(marcacao.data_hora)}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[#88A096]">Clínica</div>
            <div className="mt-1 font-bold text-[#1A1A1A]">{marcacao.clinica.nome}</div>
            <div className="text-sm text-[#5A5A5A]">{marcacao.clinica.telefone}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[#88A096]">Serviço</div>
            <div className="mt-1 font-bold text-[#1A1A1A]">{marcacao.servico.nome}</div>
            <div className="text-sm text-[#5A5A5A]">{marcacao.servico.duracao_minutos} min</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[#88A096]">Animal</div>
            <div className="mt-1 font-bold text-[#1A1A1A]">{marcacao.animal.nome}</div>
          </div>
        </div>

        <ol className="mt-7 space-y-2 text-sm text-[#1A1A1A]">
          {[
            "Recebeu um email com a confirmação",
            "A clínica irá validar a sua marcação em breve",
            "Receberá um lembrete 24h antes",
          ].map((t, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#88A096]/15 text-[#1E5F6E] text-xs font-bold">{i + 1}</span>
              {t}
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <a
          href={icsUrl(marcacao.token_cancelamento)}
          data-testid="add-to-calendar"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1E5F6E] px-6 py-3 text-sm font-bold text-white hover:bg-[#174E5B] transition-colors"
        >
          <CalendarIcon className="h-4 w-4" />
          Adicionar ao calendário
        </a>
        <Link
          to={`/marcacao/${marcacao.token_cancelamento}`}
          data-testid="manage-link"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-white border-2 border-[#1E5F6E]/15 px-6 py-3 text-sm font-bold text-[#1E5F6E] hover:border-[#1E5F6E]/40 transition-colors"
        >
          Cancelar ou reagendar
        </Link>
        <button
          onClick={onReset}
          data-testid="reset-link"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-white border-2 border-[#1E5F6E]/15 px-6 py-3 text-sm font-bold text-[#1E5F6E] hover:border-[#1E5F6E]/40 transition-colors"
        >
          Voltar à página inicial
        </button>
      </div>
    </motion.div>
  );
}

// ------- Main page --------
export default function MarcarConsultaPage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const queryClinica = search.get("clinica");

  const [step, setStep] = useState(1);
  const [clinicas, setClinicas] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [todosServicos, setTodosServicos] = useState([]);
  const [carregando, setCarregando] = useState(true);

  const [clinicaSlug, setClinicaSlug] = useState(queryClinica || null);
  const [servicoSlug, setServicoSlug] = useState(null);
  const [slotSel, setSlotSel] = useState({ date: null, hora: null });
  const [dados, setDados] = useState({
    nome: "", email: "", telefone: "", primeira_vez: null,
    animal: { nome: "", especie: "", raca: "", idade_anos: "", sexo: "" },
    motivo: "",
    consentimento_marketing: false,
    aceita_politica: false,
    website: "",
  });
  const [erros, setErros] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [erroSubmit, setErroSubmit] = useState(null);
  const [sucesso, setSucesso] = useState(null);

  useEffect(() => {
    Promise.all([listarClinicas(), listarServicosOnline(), listarTodosServicos()])
      .then(([c, s, t]) => {
        setClinicas(c);
        setServicos(s);
        setTodosServicos(t);
      })
      .finally(() => setCarregando(false));
  }, []);

  // Preselect clinic via ?clinica=
  useEffect(() => {
    if (queryClinica && clinicas.some((c) => c.slug === queryClinica)) {
      setClinicaSlug(queryClinica);
    }
  }, [queryClinica, clinicas]);

  const clinicaSel = useMemo(() => clinicas.find((c) => c.slug === clinicaSlug), [clinicas, clinicaSlug]);
  const servicoSel = useMemo(() => servicos.find((s) => s.slug === servicoSlug), [servicos, servicoSlug]);

  const validarDados = () => {
    const e = {};
    if (!dados.nome || dados.nome.trim().length < 3) e.nome = "Nome obrigatório (mín. 3 caracteres).";
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(dados.email || "")) e.email = "Email inválido.";
    const tel = (dados.telefone || "").replace(/[\s\-\.]/g, "");
    if (!(/^9\d{8}$/.test(tel) || /^\+3519\d{8}$/.test(tel) || /^003519\d{8}$/.test(tel)))
      e.telefone = "Telemóvel inválido. Use 9XX XXX XXX ou +351 9XX XXX XXX.";
    if (dados.primeira_vez === null) e.primeira_vez = "Indique se é a primeira vez.";
    if (!dados.animal.nome) e.animal_nome = "Nome do animal obrigatório.";
    if (!dados.animal.especie) e.animal_especie = "Espécie obrigatória.";
    if (!dados.aceita_politica) e.aceita_politica = "Tem de aceitar a política de privacidade.";
    setErros(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async () => {
    if (!validarDados()) return;
    setEnviando(true);
    setErroSubmit(null);
    try {
      const dataHora = combinarDataHora(slotSel.date, slotSel.hora);
      const payload = {
        clinica_slug: clinicaSlug,
        servico_slug: servicoSlug,
        data_hora: dataHora.toISOString(),
        tutor: {
          nome: dados.nome.trim(),
          email: dados.email.trim().toLowerCase(),
          telefone: dados.telefone,
          primeira_vez: dados.primeira_vez,
        },
        animal: {
          nome: dados.animal.nome.trim(),
          especie: dados.animal.especie,
          raca: dados.animal.raca || null,
          idade_anos: dados.animal.idade_anos === "" ? null : Number(dados.animal.idade_anos),
          sexo: dados.animal.sexo || null,
        },
        motivo: dados.motivo || null,
        consentimento_marketing: dados.consentimento_marketing,
        aceita_politica: dados.aceita_politica,
        website: dados.website,
      };
      const r = await criarMarcacao(payload);
      setSucesso(r);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail
        || (Array.isArray(err?.response?.data?.detail) ? err.response.data.detail[0]?.msg : null)
        || "Não foi possível criar a marcação. Tente novamente.";
      setErroSubmit(typeof msg === "string" ? msg : "Erro inesperado.");
    } finally {
      setEnviando(false);
    }
  };

  const canNext = (() => {
    if (step === 1) return !!clinicaSlug;
    if (step === 2) return !!servicoSlug;
    if (step === 3) return !!slotSel.date && !!slotSel.hora;
    if (step === 4) return true; // validation triggered on click
    return true;
  })();

  const handleNext = () => {
    if (step === 4) {
      if (!validarDados()) return;
    }
    setStep((s) => Math.min(5, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const handleBack = () => {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1A1A1A]" data-testid="marcar-page">
      <SeoHead
        title="Marcar Consulta | Vet Clinic — Madeira"
        description="Marque a sua consulta online em menos de um minuto. Ribeira Brava ou Calheta."
      />
      <Header />
      <main className="container-app py-12 md:py-16 max-w-4xl mx-auto">
        {!sucesso ? (
          <>
            <Stepper current={step} />

            {carregando ? (
              <div className="flex items-center gap-3 text-[#5A5A5A]">
                <Loader2 className="h-5 w-5 animate-spin" /> A carregar...
              </div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {step === 1 && (
                    <StepClinica clinicas={clinicas} value={clinicaSlug} onChange={setClinicaSlug} />
                  )}
                  {step === 2 && (
                    <StepServico
                      servicos={servicos}
                      todos={todosServicos}
                      clinicaSelecionada={clinicaSel}
                      value={servicoSlug}
                      onChange={setServicoSlug}
                    />
                  )}
                  {step === 3 && clinicaSlug && servicoSlug && (
                    <StepDataHora
                      clinicaSlug={clinicaSlug}
                      servicoSlug={servicoSlug}
                      value={slotSel}
                      onChange={setSlotSel}
                    />
                  )}
                  {step === 4 && (
                    <StepDados value={dados} onChange={setDados} erros={erros} />
                  )}
                  {step === 5 && clinicaSel && servicoSel && slotSel.date && slotSel.hora && (
                    <StepConfirmacao
                      clinica={clinicaSel}
                      servico={servicoSel}
                      dataHora={combinarDataHora(slotSel.date, slotSel.hora)}
                      dados={dados}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            )}

            {erroSubmit && step === 5 && (
              <div className="mt-6 rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 px-5 py-3 text-sm" data-testid="erro-submit">
                {erroSubmit}
              </div>
            )}

            {!carregando && step < 5 && (
              <NavRow onBack={step > 1 ? handleBack : null} onNext={handleNext} nextDisabled={!canNext} />
            )}
            {!carregando && step === 5 && (
              <NavRow
                onBack={handleBack}
                onNext={onSubmit}
                loading={enviando}
                nextDisabled={false}
                nextLabel="Confirmar marcação"
                nextTestId="wizard-submit"
              />
            )}
          </>
        ) : (
          <Sucesso marcacao={sucesso} onReset={() => navigate("/")} />
        )}
      </main>
      <Footer />
    </div>
  );
}
